import { Box, Button, Grid, Typography } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { styled } from '@mui/material/styles'
import Form from '@rjsf/mui'
import { IChangeEvent } from '@rjsf/mui/dist'
import { RJSFSchema, RJSFValidationError } from '@rjsf/utils/dist'
import validator from '@rjsf/validator-ajv8'
import { useCreatePresetNodeMutation } from 'apollo/configurator/mutations/CreatePresetNode.generated'
import { useUpdatePresetNodeMutation } from 'apollo/configurator/mutations/UpdatePresetNode.generated'
import { useNodeQuery } from 'apollo/configurator/queries/Node.generated'
import { useNodeOverrideSchemaQuery } from 'apollo/configurator/queries/NodeOverrideSchema.generated'
import { useUiSchemaQuery } from 'apollo/configurator/queries/UiSchema.generated'
import { addPresetNodeDataToTree } from 'components/presetManager/helpers/TreeHelperFunctions'
import { ErrorContext } from 'context/error/ErrorContext'
import { PresetModelContext } from 'context/preset-model/PresetModelContext'
import { JSONSchema7Object } from 'json-schema'
import { useNotify, useTranslate } from 'ra-core'
import { FormEvent, useContext, useEffect, useState } from 'react'
import { TreeItem } from 'react-sortable-tree'
import IconFieldTemplate from './IconFieldTemplate'

const StyledForm = styled(Form)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',

    '& > .MuiFormControl-root': {
        height: '100%',
        overflowY: 'scroll',
        overflowX: 'hidden',
        maxHeight: 'calc(100vh - 360px)',
        borderTop: '1px solid #eaeaea'
    },

    '& .MuiCheckbox-root': {
        padding: '0 8px 0 8px'
    },

    '& .MuiBox-root': {
        marginTop: 0
    },

    '& button[type="submit"]': {
        display: 'none'
    },

    '& .field.form-group': {
        width: '100%'
    },

    '.MuiFormControl-root > .MuiGrid-container > .MuiGrid-item': {
        margin: '0 16px'
    }
})

interface AttributesEditorProps {
    nodeId: number
    parentId?: string | undefined
    presetId: number
    modelCode: string
    reRenderCallback: () => void
}

const AttributesEditor = ({
    nodeId,
    presetId,
    modelCode,
    reRenderCallback
}: AttributesEditorProps) => {
    const DEFAULT_TYPE = 'root'
    const [nodeOverrideForm, setNodeOverrideForm] = useState<any>({})
    const translate = useTranslate()
    const notify = useNotify()
    const { setErrorState } = useContext(ErrorContext)
    const { state: presetModelState, setState: setPresetModelState } =
        useContext(PresetModelContext)
    const { data: { configuratorNode: rootNode } = {}, loading: rootNodeLoading } = useNodeQuery({
        variables: { id: (presetModelState.selectedNode?.id || nodeId) as any },
        onCompleted: (data) => {
            const node = data?.configuratorNode
            setNodeOverrideForm(node && JSON.parse(node.node_attributes ?? ''))
        }
    })

    useEffect(() => {
        if (rootNode) {
            setNodeOverrideForm(JSON.parse(rootNode.node_attributes ?? ''))
        }
    }, [rootNode])
    const { data: { configuratorUiSchema: uiSchema } = {}, loading: uiSchemaLoading } =
        useUiSchemaQuery()

    const {
        data: { configuratorNodeOverrideSchema: overrideSchema } = {},
        loading: overrideSchemaLoading
    } = useNodeOverrideSchemaQuery({
        variables: {
            model: modelCode,
            nodeType: nodeOverrideForm?.type ?? DEFAULT_TYPE
        }
    })

    const [createPresetNodeQuery] = useCreatePresetNodeMutation()
    const [updatePresetNodeQuery] = useUpdatePresetNodeMutation()
    const [uniqueKey, setUniqueKey] = useState<number>(0)
    const [formData, setFormData] = useState<unknown>(
        presetModelState?.selectedNode?.presetNode?.definition
    )
    const [treeState, setStreeState] = useState<TreeItem[]>(presetModelState.treeState)
    const [selectedNode, setSelectedNode] = useState<any>(presetModelState.selectedNode)
    const [isPristine, setIsPristine] = useState<boolean>(true)

    // Selected node changed. This changes formData which triggers a form re-render
    useEffect(() => {
        const newFormData = !!selectedNode?.presetNode?.definition
            ? JSON.parse(selectedNode.presetNode.definition)
            : nodeOverrideForm
        setFormData(newFormData)
        setUniqueKey(Date.now())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedNode, selectedNode?.presetNode])

    useEffect(() => {
        if (presetModelState.treeState !== treeState) setStreeState(presetModelState.treeState)
        return () => {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presetModelState.treeState])

    useEffect(() => {
        if (presetModelState.selectedNode !== selectedNode)
            setSelectedNode(presetModelState.selectedNode)
        return () => {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presetModelState.selectedNode])

    useEffect(() => {
        if (!!presetModelState.reRenderForm) {
            setPresetModelState((prev) => ({
                ...prev,
                reRenderForm: false
            }))
            reRenderCallback()
        }
        return () => {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presetModelState.reRenderForm])

    // JSON schema form overrides
    const overrideSchemaObject = overrideSchema && JSON.parse(overrideSchema)
    let uiSchemaObject = uiSchema && JSON.parse(uiSchema)
    uiSchemaObject = {
        ...uiSchemaObject,
        overrides: {
            items: {
                icon: { 'ui:field': IconFieldTemplate },
                background: { 'ui:field': IconFieldTemplate },
                printing_canvas_url: { 'ui:field': IconFieldTemplate },
                description: { 'ui:widget': 'textarea' },
                'config.value_group': { 'ui:disabled': true },
                'config.validation': { 'ui:disabled': true },

                // Defaults
                version: { 'ui:readonly': true },
                constraints: { 'ui:readonly': true },
                upsell: { 'ui:readonly': true },
                valueGroups: { 'ui:readonly': true }
            }
        }
    }

    const createPreset = async (formdata: string) => {
        const variables = {
            definition: formdata,
            parentNodeId: nodeId,
            presetId: presetId
        }
        await createPresetNodeQuery({ variables: variables })
            .then((result) => {
                const newTree = addPresetNodeDataToTree(
                    treeState,
                    selectedNode,
                    (result?.data?.configuratorCreatePresetNode as unknown as TreeItem) ??
                        ({} as TreeItem)
                )
                setPresetModelState((prev) => ({
                    ...prev,
                    hasChanges: true,
                    treeState: newTree.treeData,
                    reRenderTree: true
                }))
            })
            .finally(() => {
                notify('manager.resources.preset.added_successfully', { type: 'success' })
            })
    }

    const updatePreset = (definition: any) => {
        const id = selectedNode?.presetNode?.id
        if (id) {
            updatePresetNodeQuery({
                variables: {
                    definition: definition,
                    configuratorUpdatePresetNodeId: id
                }
            }).then(() => {
                notify('manager.resources.preset.edited_successfully', { type: 'success' })
                setPresetModelState((prev) => {
                    return {
                        ...prev,
                        hasChanges: true,
                        reRenderTree: true
                    }
                })
            })
        }
    }

    const onErrorHandler = (errors: JSONSchema7Object[]) => {
        setErrorState({ hasError: true, message: errors[0].stack as string })
    }
    const onChangeHandler = (e: IChangeEvent<unknown>) => {
        setIsPristine(false)
        if (e.formData !== formData) setFormData(e.formData)
    }
    const onSaveHandler = (
        e: IChangeEvent<any, RJSFSchema, any>,
        nativeEvent: FormEvent<HTMLFormElement>
    ) => {
        setIsPristine(true)
        nativeEvent.stopPropagation()
        const formData = e?.formData

        if (formData && formData?.overrides) {
            if (!selectedNode?.presetNode) createPreset(JSON.stringify(formData))
            else updatePreset(JSON.stringify(formData))
        } else {
            notify('manager.resources.preset.empty_submit', { type: 'error' })
        }
    }

    const TransformErrors = (errors: RJSFValidationError[]) => {
        return errors?.map((error: RJSFValidationError) => {
            console.warn(error)
            return error
        })
    }

    if (uiSchemaLoading || overrideSchemaLoading || rootNodeLoading) return <CircularProgress />

    return (
        <>
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 99,
                    bgcolor: 'white',
                    p: '16px',
                    borderBottom: '1px solid #eaeaea'
                }}
            >
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item md={6}>
                        <Typography variant="h6">{`${translate(
                            'manager.resources.preset.manager.selected_node'
                        )}: ${nodeOverrideForm?.label ?? nodeOverrideForm?.code}`}</Typography>
                    </Grid>

                    <Grid
                        item
                        md={6}
                        style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}
                    >
                        <Button
                            type="submit"
                            color="primary"
                            variant="contained"
                            disableElevation
                            disabled={isPristine}
                            form="preset-form"
                        >
                            Submit
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            <Grid container style={{ width: '100%' }}>
                <Grid item md={12} style={{ width: '100%' }}>
                    {overrideSchemaObject && nodeOverrideForm && uiSchemaObject ? (
                        <StyledForm
                            id="preset-form"
                            key={uniqueKey}
                            showErrorList={false}
                            schema={overrideSchemaObject}
                            uiSchema={uiSchemaObject}
                            formData={formData}
                            onSubmit={(e, nativeEvent) => onSaveHandler(e, nativeEvent)}
                            onChange={(e) => onChangeHandler(e)}
                            onError={(e) => onErrorHandler(e)}
                            transformErrors={TransformErrors}
                            validator={validator}
                        />
                    ) : (
                        <CircularProgress />
                    )}
                </Grid>
            </Grid>
        </>
    )
}

export default AttributesEditor
